import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { Button } from "@/components/ui/Button";
import {
  formatCurrency,
  grossAmountFromNet,
  netAmountFromGross,
} from "@/lib/money";
import type { ArticleBatch } from "@/types/camping";

export interface ArticleBatchesSectionProps {
  articleId: string;
  taxRatePercent: string;
  canEdit: boolean;
  active: boolean;
}

export function ArticleBatchesSection({
  articleId,
  taxRatePercent,
  canEdit,
  active,
}: ArticleBatchesSectionProps) {
  const [batches, setBatches] = useState<ArticleBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    batch_number: "",
    quantity: "",
    purchase_price: "",
    expires_at: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    if (!active) return;
    setLoading(true);
    api
      .get<ArticleBatch[]>(`/api/v1/articles/${articleId}/batches`)
      .then(setBatches)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [articleId, active]);

  useEffect(() => {
    load();
  }, [load]);

  async function addBatch() {
    if (!canEdit) return;
    const qty = parseFloat(form.quantity.replace(",", "."));
    if (!Number.isFinite(qty) || qty <= 0) {
      setError("Menge muss größer als 0 sein.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/v1/articles/${articleId}/batches`, {
        batch_number: form.batch_number.trim() || null,
        quantity: qty,
        purchase_price: netAmountFromGross(form.purchase_price, taxRatePercent),
        expires_at: form.expires_at || null,
      });
      setForm({ batch_number: "", quantity: "", purchase_price: "", expires_at: "" });
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Charge konnte nicht angelegt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-semibold text-label">Chargen & MHD</legend>
      {loading ? <p className="text-xs text-label-secondary">Laden…</p> : null}
      {batches.length > 0 ? (
        <ul className="space-y-2 text-sm">
          {batches.map((b) => (
            <li
              key={b.id}
              className="flex flex-wrap justify-between gap-2 rounded border border-separator px-3 py-2"
            >
              <span>
                {b.batch_number ? (
                  <span className="font-mono text-xs">{b.batch_number}</span>
                ) : (
                  "Charge"
                )}{" "}
                · Menge {b.quantity}
                {b.expires_at ? ` · MHD ${formatIsoDateShort(b.expires_at)}` : ""}
              </span>
              {b.purchase_price != null ? (
                <span className="tabular-nums text-label-secondary">
                  EK brutto{" "}
                  {formatCurrency(
                    grossAmountFromNet(b.purchase_price, taxRatePercent) ??
                      Number(b.purchase_price),
                  )}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-label-secondary">Keine Chargen erfasst.</p>
      )}
      {canEdit ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <input
            className="eh-input sm:col-span-2"
            placeholder="Chargen-Nr. (optional)"
            value={form.batch_number}
            onChange={(e) => setForm((f) => ({ ...f, batch_number: e.target.value }))}
          />
          <input
            type="number"
            step="0.001"
            min="0"
            required
            className="eh-input"
            placeholder="Menge"
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
          />
          <input
            type="date"
            className="eh-input"
            value={form.expires_at}
            onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
          />
          <input
            type="number"
            step="0.01"
            min="0"
            className="eh-input"
            placeholder="EK brutto (optional)"
            value={form.purchase_price}
            onChange={(e) => setForm((f) => ({ ...f, purchase_price: e.target.value }))}
          />
          <Button
            type="button"
            variant="secondary"
            loading={submitting}
            className="sm:col-span-2"
            onClick={() => void addBatch()}
          >
            Charge hinzufügen
          </Button>
        </div>
      ) : null}
      {error ? <p className="text-xs text-status-red">{error}</p> : null}
    </fieldset>
  );
}
